// La barre du lecteur de podcast (lot B14). Elle remplace la barre sombre à liseré ambre de src/legacy/app.js :
// même élément d'accueil (#player-bar), mêmes actions (playerToggle, playerSkip, playerSeek, playerSpeed,
// playerClose — toutes définies dans src/legacy/app.js, appelées ici par délégation), et surtout le même NOM,
// window.renderPlayerBar : les huit écouteurs de window.player.audio l'appellent, on ne les touche pas.
//
// Ce qui change, et c'est le geste du lot : la barre ne pousse plus le bas de page en écrivant
// document.body.style.paddingBottom depuis le JavaScript. Elle mesure sa propre hauteur, la pose en
// --lecteur-h, et c'est le CSS qui réserve la place — au même endroit que la barre basse et la zone sûre. Rien
// ne saute, rien n'est deviné à partir de window.innerWidth (donc rien ne se trompe après une rotation), et la
// barre glisse depuis le bas en 240 ms.
import { html, attr } from '../../core/html.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';
import { ouvrirMenu } from './menu.js';

var visible = false, sortie = null, mesureur = null;

function hauteur(px) {
    try { document.documentElement.style.setProperty('--lecteur-h', Math.round(px) + 'px'); } catch (e) {}
}
// La hauteur est relue à chaque redessin ET quand la barre change d'elle-même (rotation, titre qui passe à la
// ligne, vitesse plus large) : c'est la mesure qui fait foi, jamais une valeur écrite d'avance.
function mesurer(barre) {
    var el = barre.querySelector('.lecteur'); if (!el) return;
    if (!mesureur && window.ResizeObserver) {
        mesureur = new window.ResizeObserver(function(e) { if (visible && e[0]) hauteur(e[0].target.getBoundingClientRect().height); });
    }
    if (mesureur) { mesureur.disconnect(); mesureur.observe(el); }
    hauteur(el.getBoundingClientRect().height);
}

function etat(p, a) {
    if (p.error) return ['puce puce-danger', 'Lecture impossible'];
    if (a.ended) return ['puce puce-sans-point', 'Terminé'];
    return a.paused ? ['puce puce-sans-point', 'En pause'] : ['puce puce-ok', 'En lecture'];
}

function vitesse(v) { return String(v).replace('.', ',') + '×'; }

function vue(p, a) {
    var joue = !a.paused && !a.ended;
    var v = window.state.podcastSpeed || 1;
    var duree = window.playerDuration(), connue = duree > 0;
    var e = etat(p, a);
    return html`<div class="lecteur" role="region" aria-label="Lecteur de podcast">
        <div class="lecteur-colonne">
            <div class="lecteur-haut">
                <div class="lecteur-titre">
                    <!-- Le texte a son propre élément : sur un conteneur en flex, text-overflow ne s'applique
                         pas au nœud de texte, et un titre trop long était coupé net, sans points de suite. -->
                    <span class="lecteur-nom" title="${p.title}">${ico('headphones', { taille: 16 })}<span class="lecteur-nom-texte">${p.title}</span></span>
                    <span class="${e[0]}">${e[1]}</span>
                </div>
                <div class="lecteur-commandes">
                    <!-- Le mot du geste est lu, le chiffre est vu : le nom accessible contient le libellé visible
                         (« Reculer de 10 s »), donc la commande vocale marche aussi. -->
                    <button type="button" class="btn btn-fantome btn-sm lecteur-saut" data-action="playerSkip" data-args="${attr([-10])}" title="Reculer de 10 secondes"><span class="sr-only">Reculer de </span>${ico('arrow-counter-clockwise', { taille: 16 })}<span class="num">10 s</span></button>
                    <button type="button" class="lecteur-jouer" data-action="playerToggle" aria-label="${joue ? 'Mettre en pause' : 'Reprendre la lecture'}">${ico(joue ? 'pause' : 'play', { taille: 24 })}</button>
                    <button type="button" class="btn btn-fantome btn-sm lecteur-saut" data-action="playerSkip" data-args="${attr([15])}" title="Avancer de 15 secondes"><span class="sr-only">Avancer de </span>${ico('arrow-clockwise', { taille: 16 })}<span class="num">15 s</span></button>
                </div>
                <div class="lecteur-droite">
                    <button type="button" class="btn btn-fantome btn-sm lecteur-vitesse num" data-action="lecteurVitesse" aria-haspopup="menu" aria-expanded="false" aria-label="Vitesse de lecture : ${vitesse(v)}">${vitesse(v)}</button>
                    <button type="button" class="ico-btn" data-action="playerClose" aria-label="Fermer le lecteur" title="Fermer le lecteur">${ico('x', { taille: 20 })}</button>
                </div>
            </div>
            ${p.error ? html`<p class="lecteur-erreur">${p.error}</p>` : ''}
            <div class="lecteur-position">
                <span id="player-cur" class="lecteur-temps num">${window.fmtTime(a.currentTime)}</span>
                <input id="player-seek" class="lecteur-reglette" type="range" min="0" max="1000" value="${connue ? Math.round(a.currentTime / duree * 1000) : 0}"${connue ? '' : html` disabled`} data-input-action="lecteurSeek" aria-label="Position de lecture">
                <span id="player-dur" class="lecteur-temps num">${connue ? window.fmtTime(duree) : '--:--'}</span>
            </div>
        </div>
    </div>`;
}

// La barre sort par le bas, puis la place est rendue : l'inverse laisserait le contenu remonter sous une barre
// encore visible. À l'ouverture, la place est prise tout de suite (mesurer), au moment où la barre s'élance.
function retirer(barre) {
    if (mesureur) mesureur.disconnect();
    var el = visible ? barre.querySelector('.lecteur') : null;
    if (!el) { visible = false; hauteur(0); barre.innerHTML = ''; return; }
    visible = false;
    el.classList.add('lecteur--sort');
    clearTimeout(sortie);
    sortie = setTimeout(function() { barre.innerHTML = ''; hauteur(0); }, 260);
}

window.renderPlayerBar = function() {
    var barre = document.getElementById('player-bar'); if (!barre) return;
    var p = window.player, a = p.audio;
    if (!p.key || !a) { retirer(barre); return; }
    clearTimeout(sortie);
    var entre = !visible;
    barre.innerHTML = String(vue(p, a));
    visible = true;
    // L'entrée ne se joue qu'à l'arrivée de la barre : play / pause redessinent aussi, et la faire remonter à
    // chaque bascule serait un tic, pas une animation.
    if (entre) {
        var el = barre.querySelector('.lecteur');
        if (el) { el.classList.add('lecteur--entre'); requestAnimationFrame(function() { requestAnimationFrame(function() { el.classList.remove('lecteur--entre'); }); }); }
    }
    mesurer(barre);
};

// Le temps qui passe ne redessine pas la barre : trois nœuds suffisent, et le curseur reste où il est tant que
// c'est l'utilisateur qui le tient.
window.updatePlayerTime = function() {
    var a = window.player.audio; if (!a || !window.player.key) return;
    var cur = document.getElementById('player-cur'), dur = document.getElementById('player-dur'), regl = document.getElementById('player-seek');
    if (cur) cur.textContent = window.fmtTime(a.currentTime);
    var d = window.playerDuration();
    if (dur && d) dur.textContent = window.fmtTime(d);
    if (regl && d && document.activeElement !== regl) { regl.disabled = false; regl.value = Math.round(a.currentTime / d * 1000); }
};

// Le bouton « Écouter » d'une séance ou d'une ligne de podcast : il dit maintenant ce que fait le lecteur.
// Les rendus refaits ne posent plus data-pk (les boutons portent data-action="ecouterSeance" / "lcaEcouter"),
// mais l'attribut reste lu ici pour tout rendu qui le reposerait.
window.refreshPodcastButtons = function() {
    var p = window.player, joue = !!(p.audio && p.key && !p.audio.paused && !p.audio.ended);
    document.querySelectorAll('[data-pk]').forEach(function(el) {
        var mien = el.getAttribute('data-pk') === p.key;
        el.setAttribute('aria-pressed', mien && joue ? 'true' : 'false');
    });
};

// Les sept vitesses tenaient sur une rangée de boutons de 20 px, sous le plancher et sous la cible de 44 px.
// Elles vivent maintenant dans le menu du système, feuille basse au doigt, la vitesse courante marquée.
actions.lecteurVitesse = function(el) {
    var v = window.state.podcastSpeed || 1;
    ouvrirMenu(el, window.PLAYER_SPEEDS.map(function(s) {
        return { label: vitesse(s) + (s === 1 ? ' (normal)' : ''), courant: s === v, action: function() { window.actions.playerSpeed(s); } };
    }), { feuille: true, titre: 'Vitesse de lecture', label: 'Vitesse de lecture' });
};
actions.lecteurSeek = function(el) { window.actions.playerSeek(el.value); };

window.actions.lecteurVitesse = actions.lecteurVitesse;
window.actions.lecteurSeek = actions.lecteurSeek;
